import React, { useState } from 'react';
import { BaseModal } from '@/components/shared';
import { CollaboratorCombobox } from '@/components/shared';
import type { CollaboratorPublic } from '@/features/Collaborators/Data';
import { useAuth } from '@/contexts/AuthContext';
import type { ColaboradorPendente } from '../../../models';
import { useSolicitacaoMutations } from '../../../hooks/data';

interface VincularColaboradorModalProps {
  solicitacaoId: string;
  colaboradorPendente?: ColaboradorPendente | null;
  isOpen: boolean;
  onClose: () => void;
}

/**
 * "Vincular Colaborador" — só si_admin (firestore.rules), só na transição
 * colaboradorId null → id real, exatamente uma vez (Desenho Técnico §10.3, revisado).
 *
 * Fluxo em 2 passos: buscar/selecionar (reaproveita o CollaboratorCombobox já existente,
 * sem mecanismo novo de busca) e depois confirmar explicitamente, comparando o que foi
 * informado na abertura com o colaborador encontrado, para reduzir o risco de vincular a
 * pessoa errada — ação irreversível pela UI.
 */
export default function VincularColaboradorModal({
  solicitacaoId,
  colaboradorPendente,
  isOpen,
  onClose,
}: VincularColaboradorModalProps) {
  const { user } = useAuth();
  const { vincularColaborador } = useSolicitacaoMutations();

  const [colaboradorSelecionado, setColaboradorSelecionado] = useState<CollaboratorPublic | null>(null);
  const [etapaConfirmacao, setEtapaConfirmacao] = useState(false);

  const handleClose = () => {
    setColaboradorSelecionado(null);
    setEtapaConfirmacao(false);
    onClose();
  };

  const handleConfirmar = async () => {
    if (!colaboradorSelecionado || !user) return;

    await vincularColaborador.mutateAsync({
      id: solicitacaoId,
      colaboradorId: colaboradorSelecionado.id,
      colaboradorEmail: colaboradorSelecionado.email,
      colaboradorNome: colaboradorSelecionado.name,
      updatedBy: {
        id: user.collaboratorId ?? user.uid,
        name: user.displayName ?? user.email ?? '',
      },
    });

    handleClose();
  };

  return (
    <BaseModal isOpen={isOpen} onClose={handleClose} title="Vincular Colaborador" maxWidth="max-w-md">
      {!etapaConfirmacao ? (
        <div className="space-y-4 py-4">
          <p className="text-sm text-gray-600">
            Busque o colaborador real, já cadastrado no Radar, para vincular a esta Admissão.
          </p>
          <CollaboratorCombobox
            selectedId={colaboradorSelecionado?.id ?? null}
            onChange={setColaboradorSelecionado}
            placeholder="Buscar colaborador..."
          />
          <div className="flex justify-end gap-3">
            <button type="button" className="btn-secondary" onClick={handleClose}>
              Cancelar
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={!colaboradorSelecionado}
              onClick={() => setEtapaConfirmacao(true)}
            >
              Continuar
            </button>
          </div>
        </div>
      ) : (
        <div className="space-y-4 py-4">
          <p className="text-sm text-gray-600">
            Confirme que o colaborador encontrado é a mesma pessoa informada na abertura.
            Esta ação não pode ser desfeita nem alterada depois.
          </p>
          {(colaboradorPendente?.positionId || colaboradorPendente?.seniorityId) && (
            <p className="text-xs text-gray-500">
              Cargo/Senioridade informados na abertura serão preenchidos automaticamente no perfil.
            </p>
          )}

          <div className="grid grid-cols-2 gap-3 text-sm">
            <div className="rounded-md border border-gray-200 p-3">
              <p className="text-xs font-medium uppercase text-gray-400">Informado na abertura</p>
              <p className="mt-1 font-medium text-gray-900">{colaboradorPendente?.nome ?? '—'}</p>
              <p className="text-gray-600">{colaboradorPendente?.emailInformado ?? '—'}</p>
              {(colaboradorPendente?.positionName || colaboradorPendente?.seniorityName) && (
                <p className="mt-1 text-gray-600">
                  {[colaboradorPendente?.positionName, colaboradorPendente?.seniorityName].filter(Boolean).join(' - ')}
                </p>
              )}
            </div>
            <div className="rounded-md border border-indigo-200 bg-indigo-50 p-3">
              <p className="text-xs font-medium uppercase text-indigo-500">Colaborador encontrado</p>
              <p className="mt-1 font-medium text-gray-900">{colaboradorSelecionado?.name}</p>
              <p className="text-gray-600">{colaboradorSelecionado?.email}</p>
            </div>
          </div>

          <div className="flex justify-end gap-3">
            <button type="button" className="btn-secondary" onClick={() => setEtapaConfirmacao(false)}>
              Voltar
            </button>
            <button
              type="button"
              className="btn-primary"
              disabled={vincularColaborador.isPending}
              onClick={handleConfirmar}
            >
              {vincularColaborador.isPending ? 'Vinculando...' : 'Confirmar Vínculo'}
            </button>
          </div>
        </div>
      )}
    </BaseModal>
  );
}
